import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  mergeUnlockedRows,
  getMaskedRowIds,
  applyUnlockResponse,
  getUnlockMismatchMessage,
  type CommercialUnlockedRowMap,
} from "../utils/feedUtils";
import {
  type ICommercialFeedDataGridRow,
  type ICommercialFeedFetchParams,
  type IMaskUnlockResponse,
} from "../types";
import { useMaskUnlock } from "./useMaskUnlock";
import { useCommercialMaskUnlockPrice } from "./useCommercialMaskUnlockPrice";

interface IUnlockDialogProps {
  open: boolean;
  targetIds: number[];
  targetRows: ICommercialFeedDataGridRow[];
  unitPrice: number | null;
  priceError: string | null;
  onRetryPrice: () => void;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
}

interface IUseCommercialMaskUnlockFlowReturnType {
  mergedRows: ICommercialFeedDataGridRow[];
  maskedRowIds: Set<number>;
  handleUnlockSingle: (id: number) => void;
  handleUnlockMultiple: (ids: number[]) => void;
  unlockDialogProps: IUnlockDialogProps;
}

// pay_per_view プランのマスク解除フロー（未購入行のマスク状態管理・解除ダイアログ・解除APIの呼び出し）を
// ひとまとめに扱うフック。feedParams が変わったら解除済み行の記憶をリセットする。
export const useCommercialMaskUnlockFlow = (
  feedParams: ICommercialFeedFetchParams | null,
  rows: ICommercialFeedDataGridRow[]
): IUseCommercialMaskUnlockFlowReturnType => {
  const prevFeedParamsRef = useRef<string>("");
  const [unlockedRows, setUnlockedRows] = useState<CommercialUnlockedRowMap>(
    {}
  );

  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockTargetIds, setUnlockTargetIds] = useState<number[]>([]);
  const [unlockMismatchError, setUnlockMismatchError] = useState<string | null>(
    null
  );

  const {
    unlock,
    isLoading: isUnlocking,
    error: unlockError,
    clearError,
  } = useMaskUnlock();
  const {
    unitPrice,
    error: priceError,
    retry: retryPrice,
  } = useCommercialMaskUnlockPrice();

  useEffect(() => {
    const currentFeedParamsKey = JSON.stringify(feedParams);
    if (
      prevFeedParamsRef.current !== "" &&
      prevFeedParamsRef.current !== currentFeedParamsKey
    ) {
      setUnlockedRows({});
    }
    prevFeedParamsRef.current = currentFeedParamsKey;
  }, [feedParams]);

  const mergedRows: ICommercialFeedDataGridRow[] = useMemo(
    () => mergeUnlockedRows(rows, unlockedRows),
    [rows, unlockedRows]
  );

  const maskedRowIds = useMemo<Set<number>>(
    () => getMaskedRowIds(mergedRows),
    [mergedRows]
  );

  // 解除対象行は選択IDとmergedRowsから常に導出する（別途保持しない）ことで、
  // 解除完了後にmergedRowsが更新されてもダイアログ表示が古いまま残らないようにする。
  const unlockTargetRows = useMemo(() => {
    const idSet = new Set(unlockTargetIds);
    return mergedRows.filter((row) => idSet.has(row.id));
  }, [mergedRows, unlockTargetIds]);

  const handleOpenUnlockDialog = useCallback(
    (ids: number[]): void => {
      clearError();
      setUnlockMismatchError(null);
      setUnlockTargetIds(ids);
      setUnlockDialogOpen(true);
      // ダイアログを開くたびに単価を再取得し、セッション中の価格改定に追随する。
      // 実際の請求額は確定処理（POST）時点でサーバーが都度DBから算出するため、
      // ここでの取得はあくまで表示の鮮度を上げるためのもの。
      retryPrice();
    },
    [clearError, retryPrice]
  );

  const handleUnlockSingle = useCallback(
    (id: number): void => {
      handleOpenUnlockDialog([id]);
    },
    [handleOpenUnlockDialog]
  );

  const handleUnlockMultiple = useCallback(
    (ids: number[]): void => {
      handleOpenUnlockDialog(ids);
    },
    [handleOpenUnlockDialog]
  );

  const handleConfirmUnlock = useCallback(async (): Promise<void> => {
    const response: IMaskUnlockResponse | null = await unlock(unlockTargetIds);
    if (response === null) return;

    setUnlockedRows((prev) => applyUnlockResponse(prev, response));

    const mismatchMessage = getUnlockMismatchMessage(
      response,
      unlockTargetIds.length
    );
    if (mismatchMessage !== null) {
      setUnlockMismatchError(mismatchMessage);
      return;
    }

    setUnlockDialogOpen(false);
  }, [unlock, unlockTargetIds]);

  const handleCloseUnlockDialog = useCallback((): void => {
    setUnlockDialogOpen(false);
  }, []);

  return {
    mergedRows,
    maskedRowIds,
    handleUnlockSingle,
    handleUnlockMultiple,
    unlockDialogProps: {
      open: unlockDialogOpen,
      targetIds: unlockTargetIds,
      targetRows: unlockTargetRows,
      unitPrice,
      priceError,
      onRetryPrice: retryPrice,
      onConfirm: handleConfirmUnlock,
      onClose: handleCloseUnlockDialog,
      isLoading: isUnlocking,
      error: unlockError ?? unlockMismatchError,
    },
  };
};
